import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Card,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  FormHelperText
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

// Icons
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import SendIcon from '@mui/icons-material/Send';

import Page from '../../components/Page';
import BrudCrumbs from '../../components/BreadCrumbs';
import {
  getLoanTypeData,
  getBankDropdownData,
  getBankBranchData,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import { getReportingManagerList } from '../../store/Slice/Onboarding/OnboardingSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { BankOpsApi } from '../../utils/Apis';
import inputValidation from '../../utils/globalValidation';
import useCallCenterSelector from '../../store/Slice/CallCenterList/CallCenterListSelector';
import {
  getEmployementTypeList,
  getSubEmployementTypeList
} from '../../store/Slice/CallCenterList/CallCenterListSlice';

// ----------------------------------------------------------------------

export default function AddLoginFile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const { LoanTypeDrop, BankDrop, BankBranchDrop, CityDrop } = useGlobalSelector();
  const { EmployementTypeData, SubEmployementTypeData } = useCallCenterSelector();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      customerName: '',
      companyName: '',
      contactNumber: '',
      loanType: '',
      amount: '',
      location: '',
      cityId: '',
      bankId: '',
      branchId: '',
      relationshipManager: '',
      rmContactNumber: '',
      lanId: '',
      dsaName: '',
      source: '',
      employementType: '',
      subEmployementType: ''
    }
  });

  const employementType = watch('employementType');
  const subEmployementType = watch('subEmployementType');

  const options = useMemo(
    () => ({
      employment: Array.isArray(EmployementTypeData)
        ? EmployementTypeData.map((b) => ({
            label: b.employmentCategoryName,
            value: b.employmentCategoryId
          }))
        : [],
      subEmployment: Array.isArray(SubEmployementTypeData)
        ? SubEmployementTypeData.map((b) => ({
            label: b.employmentSubcategoryName,
            value: b.employmentSubcategoryId
          }))
        : [],
      loanType: (LoanTypeDrop || []).map((l) => ({ label: l.label, value: l.value })),
      bank: (BankDrop || []).map((b) => ({ label: b.label, value: b.value })),
      branch: (BankBranchDrop || []).map((b) => ({ label: b.label, value: b.value }))
    }),
    [EmployementTypeData, SubEmployementTypeData, LoanTypeDrop, BankDrop, BankBranchDrop]
  );

  useEffect(() => {
    if (!LoginData) return;
    const common = { companyId: LoginData.companyId, userId: LoginData.userId };
    dispatch(getLoanTypeData(common));
    dispatch(getBankDropdownData(common));
    dispatch(getEmployementTypeList({ companyId: LoginData.companyId }));
  }, [LoginData, dispatch]);

  useEffect(() => {
    if (employementType) {
      dispatch(
        getSubEmployementTypeList({
          companyId: LoginData?.companyId,
          returnConfiguredTypesOnly: true,
          employmentCategoryId: employementType
        })
      );
    }
  }, [employementType, LoginData, dispatch]);

  const handleBankChange = useCallback(
    (bankId) => {
      if (bankId) {
        dispatch(getBankBranchData(bankId));
        setValue('branchId', '');
      }
    },
    [dispatch, setValue]
  );

  const handleBranchChange = useCallback(
    (branchId) => {
      const selectedBranch = BankBranchDrop?.find((b) => b.value === branchId);
      if (selectedBranch?.cityId) {
        dispatch(getReportingManagerList({ cityId: selectedBranch.cityId }));
      }
    },
    [BankBranchDrop, dispatch]
  );

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      amount: Number(data.amount),
      applicantEmploymentType: subEmployementType || ''
    };

    const res = await dispatch(saveApiData(BankOpsApi.addLoginFile, payload));
    if (res?.status === 'success') navigate('/bankops');
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '13.5px',
      bgcolor: '#ffffff',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#0284c7' }
    }
  };

  const selectStyles = {
    borderRadius: '10px',
    fontSize: '13.5px',
    bgcolor: '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0284c7' }
  };

  return (
    <Page title="Partner Management System || Add Login File">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Header Row: Icon, Title & Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title & Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <ArticleOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Add Login File
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Upload and map login file details for customer verification.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Main Form Card Container */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            p: { xs: 2.5, md: 4 },
            bgcolor: '#ffffff',
            position: 'relative'
          }}
        >
          {/* Top-Left Pill Badge: File Details */}
          <Box sx={{ mb: 3.5 }}>
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: '#0284c7',
                color: '#ffffff',
                px: 2.5,
                py: 0.8,
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.2px',
                boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)'
              }}
            >
              File Details
            </Box>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Row 1, Col 1: Customer Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Customer Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="customerName"
                  control={control}
                  rules={{
                    required: 'Customer name is required',
                    validate: (value) => inputValidation({ target: { value } }, 'customerName')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter Customer Name"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 1, Col 2: Company Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Company Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="companyName"
                  control={control}
                  rules={{
                    required: 'Company name is required',
                    validate: (value) => inputValidation({ target: { value } }, 'companyName')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter Company Name"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ApartmentOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 1, Col 3: Contact Number */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Contact Number
                </Typography>
                <Controller
                  name="contactNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Contact Number"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 2, Col 1: Loan Type */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Loan Type <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="loanType"
                  control={control}
                  rules={{ required: 'Loan type is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        displayEmpty
                        sx={selectStyles}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#94a3b8' }}>Select Loan Type</span>;
                          }
                          return (
                            options.loanType?.find((o) => o.value === selected)?.label || selected
                          );
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                          Select Loan Type
                        </MenuItem>
                        {options.loanType?.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Row 2, Col 2: Amount */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Amount <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: 'Amount is required',
                    validate: (value) =>
                      inputValidation({ target: { value } }, 'amount', 'AMOUNT_FIELD')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Amount"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <span style={{ color: '#94a3b8', fontSize: '16px', fontWeight: 600 }}>
                              ₹
                            </span>
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 2, Col 3: Location */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Location <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="location"
                  control={control}
                  rules={{
                    required: 'Location is required',
                    validate: (value) => inputValidation({ target: { value } }, 'location')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter Location"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 3, Col 1: City */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  City <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="cityId"
                  control={control}
                  rules={{ required: 'City is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <ApartmentOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        }
                        sx={selectStyles}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#94a3b8' }}>Select City</span>;
                          }
                          return CityDrop?.find((o) => o.value === selected)?.label || selected;
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                          Select City
                        </MenuItem>
                        {CityDrop?.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Row 3, Col 2: Bank Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Bank Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="bankId"
                  control={control}
                  rules={{ required: 'Bank name is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <AccountBalanceOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        }
                        onChange={(e) => {
                          field.onChange(e);
                          handleBankChange(e.target.value);
                        }}
                        sx={selectStyles}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#94a3b8' }}>Select Bank</span>;
                          }
                          return options.bank?.find((o) => o.value === selected)?.label || selected;
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                          Select Bank
                        </MenuItem>
                        {options.bank?.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Row 3, Col 3: Branch Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Branch Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="branchId"
                  control={control}
                  rules={{ required: 'Branch name is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <AccountTreeOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        }
                        onChange={(e) => {
                          field.onChange(e);
                          handleBranchChange(e.target.value);
                        }}
                        sx={selectStyles}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#94a3b8' }}>Select Branch</span>;
                          }
                          return (
                            options.branch?.find((o) => o.value === selected)?.label || selected
                          );
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                          Select Branch
                        </MenuItem>
                        {options.branch?.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Row 4, Col 1: Relationship Manager */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Relationship Manager <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="relationshipManager"
                  control={control}
                  rules={{
                    required: 'Relationship manager is required',
                    validate: (value) =>
                      inputValidation({ target: { value } }, 'relationshipManager')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter Relationship Manager"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 4, Col 2: RM Contact Number */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  RM Contact Number
                </Typography>
                <Controller
                  name="rmContactNumber"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter RM Contact Number"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 4, Col 3: File Number */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  File Number <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="lanId"
                  control={control}
                  rules={{
                    required: 'File number is required',
                    validate: (value) => inputValidation({ target: { value } }, 'lanId')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter File Number"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DescriptionOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 5, Col 1: DSA Name */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  DSA Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="dsaName"
                  control={control}
                  rules={{
                    required: 'DSA name is required',
                    validate: (value) => inputValidation({ target: { value } }, 'dsaName')
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder="Enter DSA Name"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                      sx={inputStyles}
                    />
                  )}
                />
              </Grid>

              {/* Row 5, Col 2: Source */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Source <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="source"
                  control={control}
                  rules={{ required: 'Source is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <AccountTreeOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        }
                        sx={selectStyles}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#94a3b8' }}>Select Source</span>;
                          }
                          const map = {
                            SELF: 'Self',
                            COMPANY: 'Broker',
                            Channel: 'Channel'
                          };
                          return map[selected] || selected;
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                          Select Source
                        </MenuItem>
                        <MenuItem value="SELF" sx={{ fontSize: 13 }}>
                          Self
                        </MenuItem>
                        <MenuItem value="COMPANY" sx={{ fontSize: 13 }}>
                          Broker
                        </MenuItem>
                        <MenuItem value="Channel" sx={{ fontSize: 13 }}>
                          Channel
                        </MenuItem>
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Row 5, Col 3: Employment Type */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}>
                  Employment Type <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Controller
                  name="employementType"
                  control={control}
                  rules={{ required: 'Employment type is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        displayEmpty
                        startAdornment={
                          <InputAdornment position="start">
                            <WorkOutlineOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                          </InputAdornment>
                        }
                        sx={selectStyles}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#94a3b8' }}>Select Employment Type</span>;
                          }
                          return (
                            options.employment?.find((o) => o.value === selected)?.label || selected
                          );
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                          Select Employment Type
                        </MenuItem>
                        {options.employment?.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Optional Sub Employment Type if category has subcategories */}
              {options.subEmployment.length > 0 && employementType && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.75 }}
                  >
                    Sub Employment Type <span style={{ color: '#ef4444' }}>*</span>
                  </Typography>
                  <Controller
                    name="subEmployementType"
                    control={control}
                    rules={{ required: 'Sub Employment Type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth size="small" error={!!error}>
                        <Select
                          {...field}
                          displayEmpty
                          sx={selectStyles}
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ color: '#94a3b8' }}>Select Sub Employment</span>
                              );
                            }
                            return (
                              options.subEmployment?.find((o) => o.value === selected)?.label ||
                              selected
                            );
                          }}
                        >
                          <MenuItem value="" disabled sx={{ fontSize: 13, color: '#94a3b8' }}>
                            Select Sub Employment
                          </MenuItem>
                          {options.subEmployment?.map((option) => (
                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>

            {/* Bottom Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                endIcon={<SendIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: '#0284c7',
                  color: '#ffffff',
                  borderRadius: '10px',
                  px: 4,
                  py: 1.1,
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
                  '&:hover': {
                    bgcolor: '#0369a1'
                  }
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </form>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Typography sx={{ fontSize: '12.5px', color: '#94a3b8' }}>
            Copyright © 2026{' '}
            <span style={{ color: '#0284c7', fontWeight: 600 }}>Kalolytic Solutions</span>. All
            rights reserved.
          </Typography>
        </Box>
      </Container>
    </Page>
  );
}
